import { useEffect, useMemo } from "react";

import type {
  Event,
  Timeline as ServerTimeline,
} from "@tsmono/inspect-common/types";

import { findTimelineIndexForEvent } from "../findTimelineForDeepLink";
import {
  computeLaneFirstAnchors,
  resolveEventInBranches,
  resolveEventToSpan,
} from "../resolveMessageToEvent";
import {
  buildSpanSelectKeys,
  computeFlatSwimlaneRows,
  getAgents,
  parseSelection,
  useTimelineConfig,
  useTimelinesArray,
  useTranscriptTimeline,
  type SwimlaneRow,
  type Timeline,
  type TimelineSpan,
} from "../timeline";
import { buildBreadcrumbs } from "../timeline/components/TimelineSwimLanes";
import { correctRetryTimestamps } from "../timeline/retryOrdering";
import { kSampleTerminalEvents } from "../turnNavigation";

/** One selectable agent lane (a swimlane row or, with multiple root
 *  timelines, a timeline). Shared by the focus page and the main transcript's
 *  h/l lane navigation. */
export interface FocusLane {
  label: string;
  /** Nesting depth (0 = root) for indentation in the picker list. */
  depth: number;
  isCurrent: boolean;
  /** The lane's first turn anchor — navigating to a lane parks `?event=`
   *  here, exactly like the transcript's swimlane row clicks. */
  firstAnchorId: string | undefined;
  /** Which timeline this lane belongs to (0 when there's a single timeline).
   *  Lets the transcript route within-timeline hops through swimlane selection
   *  and cross-timeline hops through the deep-link timeline switch. */
  timelineIndex?: number;
  /** Swimlane row key, when this lane is a row of the active timeline. Absent
   *  for non-active timelines (only their root anchor is known here). */
  laneKey?: string;
}

export interface FocusLaneScope {
  /**
   * Events scoped to the focused event's swimlane row — the SAME event set
   * the transcript renders (and numbers turns over) when that row is
   * selected. Feed to `useEventNodes`.
   */
  laneEvents: Event[];
  /** All navigable lanes, in row order (with multiple root timelines, each
   *  timeline is a top-level lane and the active one nests its rows). */
  lanes: FocusLane[];
  /** Index of the focused lane in `lanes` (0 = root). */
  laneIndex: number;
  /** Root-to-lane path for the breadcrumb (last entry = focused lane). */
  lanePath: FocusLane[];
}

/**
 * Append the sample's trailing terminal events (score/error/limit) to a
 * lane's event set — lane carving scopes to a span, and the sample's outcome
 * lands AFTER the solver span; non-model events never mint turns.
 */
export function appendSampleTerminalEvents(
  events: Event[],
  laneEvents: Event[]
): Event[] {
  let lastModel = -1;
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i]!.event === "model") {
      lastModel = i;
      break;
    }
  }
  if (lastModel === -1) return laneEvents;
  const present = new Set(laneEvents.map((e) => e.uuid));
  const trailing = events
    .slice(lastModel + 1)
    .filter((e) => kSampleTerminalEvents.has(e.event) && !present.has(e.uuid));
  return trailing.length === 0 ? laneEvents : [...laneEvents, ...trailing];
}

/** Selection rule shared with the transcript's h/l lane stepping: the root
 *  row plus every row carrying an agent span. */
const isAgentLaneRow = (row: SwimlaneRow): boolean =>
  row.depth === 0 ||
  row.spans.some((s) => getAgents(s).some((a) => a.spanType === "agent"));

/**
 * Pure derivation of the focus page's lane list + breadcrumb from the active
 * timeline's swimlane rows (each root timeline is a top-level lane; the
 * active one nests its rows under it). Extracted for unit-testability.
 */
export function deriveFocusLanes(
  rows: SwimlaneRow[],
  activeRoot: TimelineSpan,
  timelines: Timeline[],
  activeIndex: number,
  selected: string | null
): { lanes: FocusLane[]; laneIndex: number; lanePath: FocusLane[] } {
  const multiTimeline = timelines.length > 1;
  const byAgentSpan = computeLaneFirstAnchors(activeRoot);
  const anchorForRow = (row: SwimlaneRow): string | undefined => {
    if (row.depth === 0 && !row.branch) return byAgentSpan.get(null);
    for (const rowSpan of row.spans) {
      for (const agent of getAgents(rowSpan)) {
        const anchor = byAgentSpan.get(agent.id);
        if (anchor) return anchor;
      }
    }
    return undefined;
  };

  // A selection key can carry a `:spanIndex` suffix (same-named iterative
  // sibling agents merge into one multi-span row); match on the parsed row key
  // so those instances resolve to their lane instead of falling back to root.
  const selectedRowKey =
    selected === null ? null : (parseSelection(selected)?.rowKey ?? selected);

  // Row lanes of the active timeline. In multi-timeline mode the root row
  // is represented by the timeline's own entry instead, and row lanes
  // nest one level under it.
  const rowDepthOffset = multiTimeline ? 1 : 0;
  const activeRootRowKey = rows.find((r) => r.depth === 0 && !r.branch)?.key;
  const rowLanes: FocusLane[] = rows
    .filter(isAgentLaneRow)
    .filter((row) => !(multiTimeline && row.depth === 0 && !row.branch))
    .map((row) => ({
      label: row.depth === 0 && !row.branch ? activeRoot.name : row.name,
      depth: row.depth + rowDepthOffset,
      isCurrent:
        selectedRowKey === null
          ? row.depth === 0 && !row.branch
          : row.key === selectedRowKey,
      firstAnchorId: anchorForRow(row),
      timelineIndex: activeIndex,
      laneKey: row.key,
    }));

  const lanes: FocusLane[] = multiTimeline
    ? timelines.flatMap((t, i) => {
        const entry: FocusLane = {
          label: t.name,
          depth: 0,
          isCurrent:
            i === activeIndex && !rowLanes.some((lane) => lane.isCurrent),
          firstAnchorId: computeLaneFirstAnchors(t.root).get(null),
          timelineIndex: i,
          // The active timeline's entry replaces its root row, so it carries
          // that row's key; other timelines are reached by their anchor only.
          laneKey: i === activeIndex ? activeRootRowKey : undefined,
        };
        return i === activeIndex ? [entry, ...rowLanes] : [entry];
      })
    : rowLanes;
  const laneIndex = Math.max(
    0,
    lanes.findIndex((l) => l.isCurrent)
  );

  // Breadcrumb shares the transcript header's derivation (buildBreadcrumbs
  // over the same rows) so the two views can't disagree.
  const crumbKey =
    selectedRowKey ?? rows.find((r) => r.depth === 0)?.key ?? null;
  const segments = buildBreadcrumbs(rows, crumbKey);
  const segLanes: FocusLane[] = segments.map((seg, i) => {
    const row = rows.find((r) => r.key === seg.key);
    return {
      label: seg.label,
      depth: (row?.depth ?? i) + rowDepthOffset,
      isCurrent: i === segments.length - 1,
      firstAnchorId: row ? anchorForRow(row) : byAgentSpan.get(null),
    };
  });
  let lanePath: FocusLane[] = segLanes;
  if (multiTimeline) {
    const active = timelines[activeIndex];
    if (active && active.name !== segLanes[0]?.label) {
      lanePath = [
        {
          label: active.name,
          depth: 0,
          isCurrent: segLanes.length === 0,
          firstAnchorId: byAgentSpan.get(null),
        },
        ...segLanes,
      ];
    }
  }
  return { lanes, laneIndex, lanePath };
}

/**
 * Swimlane scope for the focus-mode page, driven by the transcript's own
 * timeline machinery so turn numbers MATCH the main view. Everything derives
 * from `eventId` (computed, not stateful), so the scope never lags a
 * navigation.
 */
export function useFocusLaneScope(
  events: Event[],
  eventId: string | null,
  serverTimelines?: ServerTimeline[]
): FocusLaneScope {
  // Same preprocessing as useTranscriptTimeline, so the memoized timelines
  // array is built from identical inputs.
  const orderedEvents = useMemo(() => correctRetryTimestamps(events), [events]);
  const timelines = useTimelinesArray(orderedEvents, serverTimelines, {
    showEmptyBranches: false,
  });

  // Focus scopes to the same lanes the swimlanes show, so it honors the shared
  // utility-agents preference rather than hardcoding it.
  const { includeUtility, setIncludeUtility } = useTimelineConfig();

  // Which timeline the focused event lives in (petri-style logs have several
  // root agents as separate timelines).
  const activeIndex = useMemo(() => {
    if (!eventId || timelines.length <= 1) return 0;
    const i = findTimelineIndexForEvent(eventId, timelines);
    return i >= 0 ? i : 0;
  }, [eventId, timelines]);
  const activeRoot = timelines[activeIndex]?.root;

  const resolved = useMemo(() => {
    if (!eventId || !activeRoot) return null;
    return (
      resolveEventToSpan(eventId, activeRoot) ??
      resolveEventInBranches(eventId, activeRoot)
    );
  }, [eventId, activeRoot]);

  // Which swimlane row must be selected for the event list to contain the
  // focused event — the transcript's deep-link resolution (agent-span rows
  // and branch rows both count).
  const selected = useMemo(() => {
    if (!resolved) return null;
    if (resolved.branchRowKey) return resolved.branchRowKey;
    if (!resolved.agentSpanId || !activeRoot) return null;
    const rows = computeFlatSwimlaneRows(activeRoot, {
      includeUtility,
      showBranches: false,
    });
    return buildSpanSelectKeys(rows).get(resolved.agentSpanId)?.key ?? null;
  }, [resolved, activeRoot, includeUtility]);

  // Flipping the toggle recomputes `selected` (and downstream `laneEvents`),
  // completing resolution on the next render — no separate retrigger needed.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (
      !includeUtility &&
      resolved?.agentSpanId &&
      !resolved.branchRowKey &&
      selected === null
    ) {
      setIncludeUtility(true);
    }
  }, [includeUtility, resolved, selected, setIncludeUtility]);

  const {
    timeline,
    state,
    selection: { events: selectedEvents },
  } = useTranscriptTimeline({
    events,
    serverTimelines,
    timelineOptions: { includeUtility },
    timelineProps: { selected, onSelect: () => {} },
    activeTimelineProps: { activeIndex, onActiveChange: () => {} },
  });

  // Lane list + breadcrumb. Switching lanes parks `?event=` on the target
  // lane's first anchor; with multiple root timelines that anchor also
  // selects the timeline (via the event resolution above).
  const { lanes, laneIndex, lanePath } = useMemo(
    () =>
      deriveFocusLanes(
        state.rows,
        timeline.root,
        timelines,
        activeIndex,
        selected
      ),
    [state.rows, timeline.root, timelines, activeIndex, selected]
  );

  const laneEvents = useMemo(
    () => appendSampleTerminalEvents(orderedEvents, selectedEvents),
    [orderedEvents, selectedEvents]
  );
  return { laneEvents, lanes, laneIndex, lanePath };
}
